import { useTiers } from "~/api/swr/useTiers";
import { DocumentVersionStatus } from "~/config/types";
import { useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { type DocumentVersionSummary, type DocumentDetails } from "~/api/types";
import { type Option } from "~/molecules/ControlledCombobox";

interface DocumentLinkingContext {
  options: Option[];
  tierName: string;
}

interface UseDocumentLinkingProps {
  document: DocumentDetails;
  selectedTierId: string;
  selectedParentDocuments: Option[];
  selectedChildDocuments: Option[];
}

function getDocumentOptions(
  documentVersions: DocumentVersionSummary[],
  tierId: number | undefined,
  existingDocs: Option[],
  currentDocId: number,
): Option[] {
  if (!tierId) return [];

  return documentVersions
    .filter(
      (el) =>
        el.document.tier.id === tierId &&
        !existingDocs.some((doc) => doc.value === `${el.document.id}`) &&
        el.document.id !== currentDocId,
    )
    .map((el) => ({ value: `${el.document.id}`, label: el.title }));
}

export function useDocumentLinking({
  document,
  selectedTierId,
  selectedParentDocuments,
  selectedChildDocuments,
}: UseDocumentLinkingProps) {
  const { data: tiers } = useTiers();

  const { data: documentVersions } = useDocumentVersions({
    statuses: [DocumentVersionStatus.published],
  });

  const isLoading = !document || !tiers || !documentVersions;

  let parentContext: DocumentLinkingContext | null = null;
  let childContext: DocumentLinkingContext | null = null;

  if (!isLoading) {
    const tierIndex = tiers.findIndex((t) => t.id === Number(selectedTierId));
    const parentTierId = tierIndex > 0 ? tiers[tierIndex - 1]?.id : undefined;
    const childTierId =
      tierIndex < tiers.length - 1 ? tiers[tierIndex + 1]?.id : undefined;

    parentContext = parentTierId
      ? {
          options: getDocumentOptions(
            documentVersions,
            parentTierId,
            selectedParentDocuments,
            document.id,
          ),
          tierName: tiers.find((t) => t.id === parentTierId)!.title,
        }
      : null;

    childContext = childTierId
      ? {
          options: getDocumentOptions(
            documentVersions,
            childTierId,
            selectedChildDocuments,
            document.id,
          ),
          tierName: tiers.find((t) => t.id === childTierId)!.title,
        }
      : null;
  }

  return {
    parentContext,
    childContext,
    isLoading,
  };
}
